import { Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SectionHeader } from '@/components/ui/SectionHeader';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import { useEffect, useState } from 'react';
import type { CarouselApi } from '@/components/ui/carousel';

type Review = {
  name: string;
  initial: string;
  country: string;
  flag: string;
  rating: number;
  timeAgo: string;
  text: string;
  badge?: string;
};

const reviews: Review[] = [
  {
    name: 'Margaret O.',
    initial: 'M',
    country: 'United States',
    flag: '🇺🇸',
    rating: 5,
    timeAgo: '2 weeks ago',
    text: "I'll be honest — I almost didn't sign up. I'd been burned by promo sites that promised the world and delivered nothing. I sat on the Starter package for almost a month before pulling the trigger. Within three weeks of being featured, my book had more reads than the previous six months combined. I just upgraded to Growth.",
    badge: 'Upgraded to Growth',
  },
  {
    name: 'Daniel H.',
    initial: 'D',
    country: 'United Kingdom',
    flag: '🇬🇧',
    rating: 5,
    timeAgo: '1 month ago',
    text: 'Skeptical is an understatement. As an indie author you get pitched constantly. What convinced me was that they don\'t fake numbers — the dashboard shows real reads and clicks. My thriller picked up consistent engagement from readers in three different countries. Genuine platform.',
  },
  {
    name: 'Anneke V.',
    initial: 'A',
    country: 'Netherlands',
    flag: '🇳🇱',
    rating: 5,
    timeAgo: '3 weeks ago',
    text: 'I delayed for two months because I kept comparing them to bigger services. Best decision was finally trying it. The placement actually drove sales on my retail page — not just vanity views. Moved up to the Premium package last week.',
    badge: 'Upgraded to Premium',
  },
  {
    name: 'Rebecca T.',
    initial: 'R',
    country: 'Canada',
    flag: '🇨🇦',
    rating: 5,
    timeAgo: '1 week ago',
    text: "Was on the fence for ages. The team was patient, answered every question, no pressure. Once my book went into the Spotlight collection the engagement was steady — not a one-day spike like other platforms. Reasonable royalty bump too. Worth it.",
  },
  {
    name: 'Liam K.',
    initial: 'L',
    country: 'Australia',
    flag: '🇦🇺',
    rating: 5,
    timeAgo: '2 months ago',
    text: 'Stalled on signing up for almost six weeks. Finally did it after a friend showed me her analytics. My memoir reached readers I never could have on my own — particularly in the US and UK. Went straight to Growth after the first month.',
    badge: 'Upgraded to Growth',
  },
  {
    name: 'Sophie M.',
    initial: 'S',
    country: 'Ireland',
    flag: '🇮🇪',
    rating: 5,
    timeAgo: '5 weeks ago',
    text: "I'd written off paid promotion entirely before trying The Pacers Library. The difference here is the curation — readers actually trust what gets featured. My sales page traffic doubled in the first 10 days. Premium package now.",
    badge: 'Upgraded to Premium',
  },
  {
    name: 'Johan R.',
    initial: 'J',
    country: 'Germany',
    flag: '🇩🇪',
    rating: 5,
    timeAgo: '3 weeks ago',
    text: 'Took me weeks of reading their site before I trusted it. What I appreciated was no exaggerated promises. They simply said "your book will be visible to genuine readers." That is exactly what happened. Strong engagement, real clicks, real sales.',
  },
  {
    name: 'Chiamaka E.',
    initial: 'C',
    country: 'United Kingdom',
    flag: '🇬🇧',
    rating: 5,
    timeAgo: '1 month ago',
    text: "Honestly thought it was too good to be true at the price. I was wrong. The Spotlight feature kept driving readers to my book for the entire month — not just the first few days. Upgrading to Growth this week.",
    badge: 'Upgraded to Growth',
  },
  {
    name: 'Elena P.',
    initial: 'E',
    country: 'Spain',
    flag: '🇪🇸',
    rating: 5,
    timeAgo: '6 weeks ago',
    text: 'I delayed for so long because English-language promo platforms rarely treat international authors well. The Pacers team was different — responsive, professional, transparent. My poetry collection reached readers across four continents. Moved to Premium.',
    badge: 'Upgraded to Premium',
  },
];

const StarRow = ({ count }: { count: number }) => (
  <div className="flex items-center gap-0.5" role="img" aria-label={`${count} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={14}
        className={i < count ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}
      />
    ))}
  </div>
);

const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export const GoogleReviews = () => {
  const [api, setApi] = useState<CarouselApi>();

  useEffect(() => {
    if (!api) return;
    const id = setInterval(() => {
      if (api.canScrollNext()) api.scrollNext();
      else api.scrollTo(0);
    }, 5000);
    return () => clearInterval(id);
  }, [api]);

  return (
    <section className="section-padding bg-card/30">
      <div className="container-library">
        <SectionHeader
          label="Google Reviews"
          title="What Authors Are Saying"
          description="Verified reviews from authors around the world — including those who hesitated before joining."
        />

        <div className="flex items-center justify-center gap-3 mb-10 -mt-4 flex-wrap">
          <GoogleLogo />
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">4.9</span> out of 5
          </span>
          <StarRow count={5} />
          <span className="text-sm text-muted-foreground">· Based on verified author reviews</span>
        </div>

        <Carousel
          setApi={setApi}
          opts={{ align: 'start', loop: true }}
          className="px-4 md:px-12"
        >
          <CarouselContent>
            {reviews.map((r, i) => (
              <CarouselItem key={i} className="md:basis-1/2 lg:basis-1/3">
                <article className="bg-card border border-border rounded-lg p-6 hover:border-primary/30 transition-all duration-500 flex flex-col h-full">
                  <header className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-serif text-lg flex-shrink-0">
                      {r.initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground truncate">{r.name}</p>
                        <GoogleLogo />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <span className="mr-1">{r.flag}</span>
                        {r.country}
                      </p>
                    </div>
                  </header>

                  <div className="flex items-center gap-2 mb-3">
                    <StarRow count={r.rating} />
                    <span className="text-xs text-muted-foreground">{r.timeAgo}</span>
                  </div>

                  <p className="text-sm text-secondary-foreground leading-relaxed flex-1">
                    {r.text}
                  </p>

                  {r.badge && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <span className="inline-flex items-center text-xs font-medium text-primary">
                        ✓ {r.badge}
                      </span>
                    </div>
                  )}
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>

        <div className="mt-10 text-center">
          <Link
            to="/testimonials"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Read more testimonials
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};
